import React, { useState, useEffect, useRef } from 'react';
import { Card, message, Button, Space, Tabs } from 'antd';
import {
  PlusOutlined,
  FileExcelOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import MaturityScheduleService, { MaturityScheduleStats, PagedResponse } from '../service/maturity-schedule-service/MaturitySchedule.service';
import DeletedMaturityScheduleService from '../service/maturity-schedule-service/DeletedMaturitySchedule.service';
import BankService from '../service/bank-service/Bank.service';
import {
  MaturityScheduleResponse,
  MaturityScheduleWithTransactionInfo,
  MaturityScheduleFilter,
} from '../model/MaturitySchedule.model';
import { BankResponse } from '../model/Bank.model';
import { DeletedMaturityScheduleResponse } from '../model/DeletedMaturitySchedule.model';
import MaturityHeader from '../component/maturity-schedule/MaturityHeader';
import MaturityStatistics from '../component/maturity-schedule/MaturityStatistics';
import MaturityTable from '../component/maturity-schedule/MaturityTable';
import DeletedMaturityTable from '../component/maturity-schedule/DeletedMaturityTable';
import RenewalPeriodsModal from '../component/maturity-schedule/RenewalPeriodsModal';
import MultiExportModal from '../component/maturity-schedule/MultiExportModal';
import useEmployee from '../hook/useEmployee';
import { useIsAdmin } from '../hook/useRole';

const { TabPane } = Tabs;

const MaturitySchedule = () => {
  const [selectedMonth, setSelectedMonth] = useState<number>(
    dayjs().month() + 1
  );
  const [selectedYear, setSelectedYear] = useState<number>(dayjs().year());
  const [maturitySchedules, setMaturitySchedules] = useState<
    MaturityScheduleResponse[]
  >([]);
  const [deletedMaturitySchedules, setDeletedMaturitySchedules] = useState<
    DeletedMaturityScheduleResponse[]
  >([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [deletedLoading, setDeletedLoading] = useState<boolean>(false);
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [multiExportModalVisible, setMultiExportModalVisible] =
    useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>('active');
  const [stats, setStats] = useState<MaturityScheduleStats | null>(null);
  const [fullMaturitySchedules, setFullMaturitySchedules] = useState<
    MaturityScheduleResponse[]
  >([]);
  const [banks, setBanks] = useState<BankResponse[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [filters, setFilters] = useState<MaturityScheduleFilter>({});
  const fetchIdRef = useRef<number>(0);

  // Lấy thông tin employee đang đăng nhập
  const { employee } = useEmployee();
  const isAdmin = useIsAdmin();

  // Function to convert MaturityScheduleWithTransactionInfo to MaturityScheduleResponse
  const convertToMaturityScheduleResponse = (
    dto: MaturityScheduleWithTransactionInfo
  ): MaturityScheduleResponse => {
    return {
      id: dto.id,
      createdAt: '', // Not provided by DTO, but not critical for display
      updatedAt: '', // Not provided by DTO, but not critical for display
      deletedAt: null,
      dueDate: dto.dueDate,
      repaymentAmount: dto.repaymentAmount,
      status: dto.status,
      feeApplied: dto.feeApplied,
      creditCard: dto.creditCard, // Use the creditCard from DTO directly
      transactions: [], // Not provided by DTO since we have calculated totals
      employees: dto.employees,
      notes: dto.notes,
      isChecked: false, // Default value
      commissionRateForPartner: 0, // Not provided by DTO
      createdBy: dto.createdBy,
      createdByName: dto.createdByName,
      totalDepositAmount: dto.totalDepositAmount,
      totalWithdrawalAmount: dto.totalWithdrawalAmount,
      remainingAmount: dto.remainingAmount,
      balanceDifference: dto.balanceDifference,
      totalPaymentAmount: dto.totalPaymentAmount,
    };
  };

  // Fetch data khi component mount hoặc khi các tham số lọc thay đổi
  useEffect(() => {
    if (employee?.id) {
      fetchMaturitySchedules(pagination.current, pagination.pageSize, filters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedMonth,
    selectedYear,
    employee?.id,
    isAdmin,
    filters,
    pagination.current,
    pagination.pageSize,
  ]);

  // Fetch bank list once for filters
  useEffect(() => {
    const fetchBanks = async () => {
      try {
        const bankData = await BankService.getBankList();
        setBanks(bankData);
      } catch (error) {
        console.error('Error fetching banks for filter:', error);
      }
    };
    fetchBanks();
  }, []);

  const fetchMaturitySchedules = async (
    page: number = pagination.current,
    pageSize: number = pagination.pageSize,
    currentFilters: MaturityScheduleFilter = filters
  ) => {
    if (!employee?.id) {
      console.warn(
        'Employee not loaded yet, skipping maturity schedules fetch'
      );
      return;
    }

    const currentFetchId = ++fetchIdRef.current;

    try {
      setLoading(true);
      let pagedData: PagedResponse<MaturityScheduleWithTransactionInfo>;

      if (isAdmin) {
        // Admin có thể xem tất cả lịch đáo hạn
        pagedData = await MaturityScheduleService.getAllMaturityScheduleByMonth(
          selectedMonth,
          selectedYear,
          page - 1, // API is 0-indexed
          pageSize,
          currentFilters
        );
      } else {
        // Employee chỉ xem lịch đáo hạn mà mình được phụ trách
        pagedData =
          await MaturityScheduleService.getAllMaturityScheduleByMonthAndEmployee(
            selectedMonth,
            selectedYear,
            employee.id,
            page - 1, // API is 0-indexed
            pageSize,
            currentFilters
          );
      }

      // If a newer fetch has started, ignore this response
      if (currentFetchId !== fetchIdRef.current) return;

      // Fetch stats separately - only if we're still the latest fetch
      const statsData = await MaturityScheduleService.getMaturityScheduleStats(
        selectedMonth,
        selectedYear,
        isAdmin ? undefined : employee.id
      );

      if (currentFetchId !== fetchIdRef.current) return;

      setStats(statsData);

      // Convert the DTO data to the expected format
      const convertedData = pagedData.content.map(
        convertToMaturityScheduleResponse
      );
      setMaturitySchedules(convertedData);
      setPagination((prev) => ({
        ...prev,
        current: page,
        pageSize: pageSize,
        total: pagedData.totalElements,
      }));
    } catch (error) {
      // Only show error if we're still the latest fetch
      if (currentFetchId === fetchIdRef.current) {
        message.error('Không thể tải dữ liệu kỳ đáo hạn');
        console.error('Error fetching maturity schedules:', error);
      }
    } finally {
      if (currentFetchId === fetchIdRef.current) {
        setLoading(false);
      }
    }
  };

  const handleTableChange = (
    newPagination: any,
    tableFilters: any,
    _sorter: any
  ) => {
    // Map Ant Design filters to our MaturityScheduleFilter
    const mappedFilters: MaturityScheduleFilter = {
      ...filters, // Keep existing search keywords
      bankIds: (tableFilters.bank as number[]) || undefined,
      cyclePaymentDays: (tableFilters.cyclePaymentDate as number[]) || undefined,
      statuses: (tableFilters.status as string[]) || undefined,
      
      // Card Number / Holder search (dataIndex is creditCard)
      keyword: tableFilters.creditCard?.[0] || filters.keyword,

      // Column Search Fields (they use confirm() which triggers Table onChange)
      // Range Search for Due Date
      dueDateFrom: tableFilters.dueDate?.[0]?.[0]
        ? tableFilters.dueDate[0][0].format('YYYY-MM-DD')
        : undefined,
      dueDateTo: tableFilters.dueDate?.[0]?.[1]
        ? tableFilters.dueDate[0][1].format('YYYY-MM-DD')
        : undefined,
      repaymentAmountContains: tableFilters.repaymentAmount?.[0] || undefined,
      remainingAmountContains: tableFilters.remainingAmount?.[0] || undefined,
      totalDepositContains: tableFilters.totalDeposit?.[0] || undefined,
      totalWithdrawalContains: tableFilters.totalWithdrawal?.[0] || undefined,
      balanceDifferenceContains: tableFilters.balanceDifference?.[0] || undefined,
      totalAmountContains: tableFilters.totalAmount?.[0] || undefined,
      paymentAmountContains: tableFilters.paymentAmount?.[0] || undefined,
      
      // Keyword searches for complex columns
      employeeNames: tableFilters.employees?.[0] || undefined,
      creatorName: tableFilters.createdBy?.[0] || undefined,
    };

    setFilters(mappedFilters);
    setPagination((prev) => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    }));
  };

  const handleSearch = (newKeyword: string) => {
    setFilters((prev) => ({ ...prev, keyword: newKeyword }));
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleClearFilters = () => {
    setFilters({});
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const fetchFullMaturitySchedules = async () => {
    if (!employee?.id) return;
    try {
      let data: MaturityScheduleWithTransactionInfo[];
      if (isAdmin) {
        data = await MaturityScheduleService.getAllMaturityScheduleByMonthAll(
          selectedMonth,
          selectedYear
        );
      } else {
        data = await MaturityScheduleService.getAllMaturityScheduleByMonthAndEmployeeAll(
          selectedMonth,
          selectedYear,
          employee.id
        );
      }
      setFullMaturitySchedules(data.map(convertToMaturityScheduleResponse));
    } catch (error) {
      console.error('Error fetching full maturity schedules:', error);
    }
  };

  useEffect(() => {
    if (multiExportModalVisible) {
      fetchFullMaturitySchedules();
    }
  }, [multiExportModalVisible]);
  const fetchDeletedMaturitySchedules = async () => {
    try {
      setDeletedLoading(true);
      const data =
        await DeletedMaturityScheduleService.getDeletedMaturitySchedulesByMonth(
          selectedMonth,
          selectedYear
        );
      setDeletedMaturitySchedules(data);
    } catch (error) {
      message.error('Không thể tải dữ liệu kỳ đáo hạn đã xóa');
      console.error('Error fetching deleted maturity schedules:', error);
    } finally {
      setDeletedLoading(false);
    }
  };

  const handleDateChange = (date: dayjs.Dayjs | null) => {
    if (date) {
      setSelectedMonth(date.month() + 1);
      setSelectedYear(date.year());
    }
  };

  const handleModalSuccess = () => {
    // Refresh data after successful creation
    fetchMaturitySchedules();
  };

  const handleTabChange = (key: string) => {
    setActiveTab(key);
    if (key === 'deleted') {
      fetchDeletedMaturitySchedules();
    }
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <Card className="shadow-md">
        {/* Header */}
        <MaturityHeader
          selectedMonth={selectedMonth}
          selectedYear={selectedYear}
          onDateChange={handleDateChange}
        />

        {/* Statistics - only show for active tab */}
        {activeTab === 'active' && <MaturityStatistics stats={stats} />}

        {/* Tabs */}
        <Tabs activeKey={activeTab} onChange={handleTabChange} className="mt-6">
          <TabPane tab="Kỳ đáo hạn hiện tại" key="active">
            {/* Action Buttons */}
            <div className="mb-6 flex justify-end">
              <Space>
                <Button
                  type="default"
                  icon={<FileExcelOutlined />}
                  onClick={() => setMultiExportModalVisible(true)}
                  size="middle"
                  className="bg-green-600 hover:bg-green-700 text-white border-green-600 hover:border-green-700"
                  disabled={maturitySchedules.length === 0}
                >
                  Xuất Excel nhiều kỳ
                </Button>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setModalVisible(true)}
                  size="middle"
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  Tạo kỳ đáo hạn mới
                </Button>
              </Space>
            </div>

            {/* Active Table */}
            <MaturityTable
              maturitySchedules={maturitySchedules}
              loading={loading}
              onTransactionSuccess={() => fetchMaturitySchedules(pagination.current, pagination.pageSize)}
              currentEmployeeId={employee?.id || 1}
              isAdmin={isAdmin}
              pagination={pagination}
              onTableChange={handleTableChange}
              onSearch={handleSearch}
              onClearFilters={handleClearFilters}
              banks={banks}
            />
          </TabPane>

          <TabPane
            tab={
              <span>
                <DeleteOutlined />
                Kỳ đáo hạn đã xóa
              </span>
            }
            key="deleted"
          >
            <div className="mb-4">
              <p className="text-gray-600 text-sm">
                Danh sách các kỳ đáo hạn đã bị xóa. Khi mở rộng, bạn có thể xem
                các giao dịch và thanh toán liên quan đã bị xóa.
              </p>
            </div>

            {/* Deleted Table */}
            <DeletedMaturityTable
              deletedSchedules={deletedMaturitySchedules}
              loading={deletedLoading}
            />
          </TabPane>
        </Tabs>

        {/* Renewal Periods Modal */}
        <RenewalPeriodsModal
          visible={modalVisible}
          onClose={() => setModalVisible(false)}
          onSuccess={handleModalSuccess}
        />

        {/* Multi Export Modal */}
        <MultiExportModal
          visible={multiExportModalVisible}
          onClose={() => setMultiExportModalVisible(false)}
          maturitySchedules={fullMaturitySchedules}
          selectedMonth={selectedMonth}
          selectedYear={selectedYear}
        />
      </Card>
    </div>
  );
};

export default MaturitySchedule;
